import React, { useState, useEffect, useContext, useRef, useMemo, useCallback } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { 
  Scale, Send, ArrowLeft, BookOpen, Gavel, FileText, 
  ExternalLink, Clock, RefreshCw, Search, ScrollText, Zap, Info
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { AuthContext } from "@/App";

// Use relative URL in production (deployed), or env variable in development
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Cache for term definitions to avoid repeated API calls
const termDefinitionCache = new Map();

const ChatConsole = () => {
  const navigate = useNavigate();
  const { chatId } = useParams();
  const location = useLocation();
  const { user } = useContext(AuthContext);
  
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [currentChatId, setCurrentChatId] = useState(chatId || null);
  const [chatType, setChatType] = useState(location.state?.chatType || "research");
  const [workspaceId, setWorkspaceId] = useState(location.state?.workspaceId || null);
  const [citations, setCitations] = useState([]);
  const [searchResults, setSearchResults] = useState(null);
  const [streamingMode, setStreamingMode] = useState(true);
  const [streamingContent, setStreamingContent] = useState("");
  const [showTermTooltips, setShowTermTooltips] = useState(true);
  
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const abortControllerRef = useRef(null);

  useEffect(() => {
    if (chatId) {
      fetchChatMessages(chatId);
    }
    // Get default workspace if not set
    if (!workspaceId) {
      fetchDefaultWorkspace();
    }
  }, [chatId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, streamingContent]);

  const fetchDefaultWorkspace = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/workspaces`);
      if (res.data.length > 0) {
        setWorkspaceId(res.data[0].workspace_id);
      }
    } catch (error) {
      console.error("Error fetching workspaces:", error);
    }
  };

  const fetchChatMessages = async (id) => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/chats/${id}/messages`);
      setMessages(res.data);
      // Extract citations from messages
      const allCitations = res.data
        .filter(m => m.citations && m.citations.length > 0)
        .flatMap(m => m.citations);
      setCitations(allCitations);
    } catch (error) {
      console.error("Error fetching messages:", error);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleStreamingSend = async (userMessage) => {
    setStreamingContent("");
    
    try {
      abortControllerRef.current = new AbortController();
      
      const response = await fetch(`${BACKEND_URL}/api/chat/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          message: userMessage,
          chat_id: currentChatId,
          workspace_id: workspaceId,
          chat_type: chatType
        }),
        signal: abortControllerRef.current.signal
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let fullContent = "";
      let newChatId = currentChatId;
      let receivedCitations = [];

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        
        const chunk = decoder.decode(value);
        const lines = chunk.split('\n');
        
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6);
            if (data === '[DONE]') continue;
            
            try {
              const parsed = JSON.parse(data);
              
              if (parsed.type === 'start') {
                newChatId = parsed.chat_id;
                if (!currentChatId && newChatId) {
                  setCurrentChatId(newChatId);
                  navigate(`/chat/${newChatId}`, { replace: true });
                }
              } else if (parsed.type === 'token') {
                fullContent += parsed.content;
                setStreamingContent(fullContent);
              } else if (parsed.type === 'complete') {
                receivedCitations = parsed.citations || [];
              } else if (parsed.type === 'error') {
                throw new Error(parsed.message);
              }
            } catch (e) {
              // Skip parse errors for incomplete JSON
            }
          }
        }
      }

      // Add completed message
      const assistantMsg = {
        message_id: `msg_${Date.now()}`,
        role: "assistant",
        content: fullContent,
        citations: receivedCitations,
        created_at: new Date().toISOString()
      };
      
      setMessages(prev => [...prev, assistantMsg]);
      setStreamingContent("");
      
      if (receivedCitations.length > 0) {
        setCitations(prev => [...prev, ...receivedCitations]);
      }
      
    } catch (error) {
      if (error.name === 'AbortError') {
        console.log('Request was aborted');
      } else {
        console.error("Streaming error:", error);
        toast.error("Failed to get streaming response. Falling back to regular mode.");
        // Fallback to non-streaming
        await handleRegularSend(userMessage);
      }
    }
  };

  const handleRegularSend = async (userMessage) => {
    try {
      const res = await axios.post(`${BACKEND_URL}/api/chat`, {
        message: userMessage,
        chat_id: currentChatId,
        workspace_id: workspaceId,
        chat_type: chatType
      });

      // Update chat ID if new chat
      if (!currentChatId && res.data.chat_id) {
        setCurrentChatId(res.data.chat_id);
        navigate(`/chat/${res.data.chat_id}`, { replace: true });
      }

      // Add assistant message
      setMessages(prev => [...prev, res.data.message]);

      // Update search results and citations
      if (res.data.search_results) {
        setSearchResults(res.data.search_results);
        const newCitations = [
          ...(res.data.search_results.case_law || []),
          ...(res.data.search_results.legislation || [])
        ];
        setCitations(prev => [...prev, ...newCitations]);
      }

    } catch (error) {
      console.error("Chat error:", error);
      toast.error("Failed to send message");
      throw error;
    }
  };

  const handleSend = async () => {
    if (!input.trim() || loading) return;
    
    const userMessage = input.trim();
    setInput("");
    setLoading(true);

    // Add user message to UI immediately
    const tempUserMsg = {
      message_id: `user_${Date.now()}`,
      role: "user",
      content: userMessage,
      created_at: new Date().toISOString()
    };
    setMessages(prev => [...prev, tempUserMsg]);

    try {
      if (streamingMode) {
        await handleStreamingSend(userMessage);
      } else {
        await handleRegularSend(userMessage);
      }
    } catch (error) {
      // Error already handled in respective functions
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="min-h-screen bg-[#0B0C10] flex flex-col" data-testid="chat-console">
      {/* Header */}
      <header className="border-b border-white/10 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate("/dashboard")}
            data-testid="back-to-dashboard"
            className="text-[#94A3B8] hover:text-[#E2E8F0]"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-3">
            <Scale className="w-6 h-6 text-[#C5A059]" />
            <span className="font-playfair text-lg font-semibold text-[#E2E8F0]">TALC</span>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          {/* Terminology Tooltips Toggle */}
          <Button
            variant={showTermTooltips ? "default" : "outline"}
            size="sm"
            onClick={() => setShowTermTooltips(!showTermTooltips)}
            data-testid="terminology-toggle"
            className={`rounded-sm text-xs ${showTermTooltips 
              ? 'bg-[#C5A059]/20 text-[#C5A059] border-[#C5A059]/30 hover:bg-[#C5A059]/30' 
              : 'border-white/20 text-[#94A3B8] hover:bg-white/5'}`}
          >
            <Info className="w-3 h-3 mr-1" /> 
            {showTermTooltips ? 'Terms' : 'Terms Off'}
          </Button>
          
          {/* Streaming Toggle */}
          <Button
            variant={streamingMode ? "default" : "outline"}
            size="sm"
            onClick={() => setStreamingMode(!streamingMode)}
            data-testid="streaming-toggle"
            className={`rounded-sm text-xs ${streamingMode 
              ? 'bg-[#10B981]/20 text-[#10B981] border-[#10B981]/30 hover:bg-[#10B981]/30' 
              : 'border-white/20 text-[#94A3B8] hover:bg-white/5'}`}
          >
            <Zap className="w-3 h-3 mr-1" /> 
            {streamingMode ? 'Streaming' : 'Standard'}
          </Button>
          
          {currentChatId && messages.length > 0 && (
            <Button 
              variant="outline"
              onClick={() => navigate(`/memo/${currentChatId}`)}
              data-testid="build-memo-btn"
              className="border-white/20 text-[#E2E8F0] hover:bg-white/5 rounded-sm text-xs uppercase tracking-wide"
            >
              <ScrollText className="w-4 h-4 mr-1" /> Build Memo
            </Button>
          )}
          
          <Tabs value={chatType} onValueChange={setChatType} className="w-auto">
            <TabsList className="bg-[#15171C] border border-white/10 rounded-sm">
              <TabsTrigger 
                value="research" 
                data-testid="tab-research"
                className="data-[state=active]:bg-[#C5A059]/10 data-[state=active]:text-[#C5A059] rounded-sm text-xs uppercase tracking-wide"
              >
                <Search className="w-4 h-4 mr-1" /> Research
              </TabsTrigger>
              <TabsTrigger 
                value="drafting"
                data-testid="tab-drafting"
                className="data-[state=active]:bg-[#C5A059]/10 data-[state=active]:text-[#C5A059] rounded-sm text-xs uppercase tracking-wide"
              >
                <FileText className="w-4 h-4 mr-1" /> Drafting
              </TabsTrigger>
              <TabsTrigger 
                value="review"
                data-testid="tab-review"
                className="data-[state=active]:bg-[#C5A059]/10 data-[state=active]:text-[#C5A059] rounded-sm text-xs uppercase tracking-wide"
              >
                <Gavel className="w-4 h-4 mr-1" /> Review
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      </header>

      {/* Main Chat Area */}
      <div className="flex-1 flex overflow-hidden">
        {/* Chat Messages */}
        <div className="flex-1 flex flex-col">
          <ScrollArea className="flex-1 p-6">
            {messages.length === 0 && !streamingContent ? (
              <div className="h-full flex items-center justify-center">
                <div className="text-center max-w-md">
                  <Scale className="w-12 h-12 text-[#C5A059] mx-auto mb-4" />
                  <h2 className="font-playfair text-2xl text-[#E2E8F0] mb-2">
                    {chatType === "research" && "Legal Research Assistant"}
                    {chatType === "drafting" && "Document Drafting Assistant"}
                    {chatType === "review" && "Contract Review Assistant"}
                  </h2>
                  <p className="text-[#94A3B8] font-manrope">
                    {chatType === "research" && "Search UK case law and legislation. Ask any legal question."}
                    {chatType === "drafting" && "Draft advice notes, letters, clauses, and legal documents."}
                    {chatType === "review" && "Analyze contracts and identify risks and issues."}
                  </p>
                  {streamingMode && (
                    <div className="mt-4 flex items-center justify-center gap-2 text-[#10B981] text-sm">
                      <Zap className="w-4 h-4" />
                      <span>Real-time streaming enabled</span>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="space-y-6 max-w-4xl mx-auto">
                {messages.map((msg) => (
                  <MessageBubble key={msg.message_id} message={msg} showTermTooltips={showTermTooltips} />
                ))}
                
                {/* Streaming content in progress */}
                {streamingContent && (
                  <div className="flex items-start gap-4">
                    <div className="w-8 h-8 rounded-sm bg-[#C5A059] flex items-center justify-center">
                      <Scale className="w-4 h-4 text-[#0B0C10]" />
                    </div>
                    <div className="flex-1 bg-[#15171C] rounded-sm p-4 border border-white/5">
                      <div className="text-[#E2E8F0] font-manrope text-sm whitespace-pre-wrap">
                        {streamingContent}
                        <span className="inline-block w-2 h-4 bg-[#C5A059] animate-pulse ml-1"></span>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Loading indicator for non-streaming */}
                {loading && !streamingContent && (
                  <div className="flex items-start gap-4">
                    <div className="w-8 h-8 rounded-sm bg-[#C5A059] flex items-center justify-center">
                      <Scale className="w-4 h-4 text-[#0B0C10]" />
                    </div>
                    <div className="flex-1 bg-[#15171C] rounded-sm p-4 border border-white/5">
                      <div className="flex items-center gap-2 text-[#94A3B8]">
                        <RefreshCw className="w-4 h-4 animate-spin" />
                        <span className="font-mono text-sm">Analyzing...</span>
                      </div>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>
            )}
          </ScrollArea>

          {/* Input Area */}
          <div className="border-t border-white/10 p-4">
            <div className="max-w-4xl mx-auto">
              <div className="flex gap-3">
                <div className="flex-1 relative">
                  <textarea
                    ref={inputRef}
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder={
                      chatType === "research" ? "Ask a legal question or search UK case law..." :
                      chatType === "drafting" ? "Describe the document you want to draft..." :
                      "Describe what you want to review..."
                    }
                    data-testid="chat-input"
                    className="w-full bg-[#0B0C10] border border-white/10 rounded-sm px-4 py-3 text-[#E2E8F0] font-mono text-sm placeholder:text-[#64748B] focus:border-[#C5A059] focus:outline-none resize-none"
                    rows={2}
                    disabled={loading}
                  />
                </div>
                <Button
                  onClick={handleSend}
                  disabled={!input.trim() || loading}
                  data-testid="send-message-btn"
                  className="bg-[#C5A059] text-[#0B0C10] hover:bg-[#D4AF37] rounded-sm px-6 h-auto disabled:opacity-50"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </div>
              <p className="text-[#64748B] font-mono text-xs mt-2">
                Press Enter to send • Shift+Enter for new line
              </p>
            </div>
          </div>
        </div>

        {/* Citations Sidebar */}
        <aside className="w-80 border-l border-white/10 bg-[#15171C] hidden lg:block">
          <div className="p-4 border-b border-white/10">
            <h3 className="font-playfair text-lg text-[#E2E8F0]">Authorities</h3>
            <p className="text-[#64748B] font-mono text-xs mt-1">Referenced sources</p>
          </div>
          <ScrollArea className="h-[calc(100vh-200px)]">
            <div className="p-4 space-y-3">
              {citations.length === 0 ? (
                <p className="text-[#64748B] text-sm font-manrope">
                  Citations will appear here as you conduct research.
                </p>
              ) : (
                citations.map((cite, idx) => (
                  <CitationCard key={idx} citation={cite} />
                ))
              )}
            </div>
          </ScrollArea>
        </aside>
      </div>
    </div>
  );
};

const MessageBubble = ({ message, showTermTooltips = true }) => {
  const isUser = message.role === "user";
  
  return (
    <div className={`flex items-start gap-4 ${isUser ? 'flex-row-reverse' : ''}`}>
      <div className={`w-8 h-8 rounded-sm flex items-center justify-center shrink-0 ${
        isUser ? 'bg-[#4A6FA5]' : 'bg-[#C5A059]'
      }`}>
        {isUser ? (
          <span className="text-white font-medium text-sm">U</span>
        ) : (
          <Scale className="w-4 h-4 text-[#0B0C10]" />
        )}
      </div>
      <div className={`flex-1 ${isUser ? 'text-right' : ''}`}>
        <div className={`inline-block rounded-sm p-4 max-w-full ${
          isUser 
            ? 'bg-[#4A6FA5]/20 border border-[#4A6FA5]/30 text-left' 
            : 'bg-[#15171C] border border-white/5'
        }`}>
          <div className="text-[#E2E8F0] font-manrope text-sm whitespace-pre-wrap markdown-content">
            {!isUser && showTermTooltips ? (
              <LegalTermText text={message.content} />
            ) : (
              message.content
            )}
          </div>
        </div>
        <p className="text-[#64748B] font-mono text-xs mt-2 flex items-center gap-1 justify-end">
          <Clock className="w-3 h-3" />
          {new Date(message.created_at).toLocaleTimeString()}
        </p>
      </div>
    </div>
  );
};

// Component to render text with legal term tooltips
const LegalTermText = ({ text }) => {
  const [detectedTerms, setDetectedTerms] = useState([]);
  const [termDefinitions, setTermDefinitions] = useState({});
  const [isProcessed, setIsProcessed] = useState(false);

  useEffect(() => {
    const detectTerms = async () => {
      try {
        // Check cache first
        const cacheKey = text.substring(0, 200);
        if (termDefinitionCache.has(cacheKey)) {
          const cached = termDefinitionCache.get(cacheKey);
          setDetectedTerms(cached.terms);
          setTermDefinitions(cached.definitions);
          setIsProcessed(true);
          return;
        }

        const res = await axios.post(`${BACKEND_URL}/api/terminology/detect`, {
          text: text,
          max_terms: 20
        });
        
        if (res.data.terms && res.data.terms.length > 0) {
          setDetectedTerms(res.data.terms);
          
          // Fetch definitions for detected terms
          const definitions = {};
          for (const term of res.data.terms.slice(0, 10)) {
            try {
              const defRes = await axios.get(
                `${BACKEND_URL}/api/terminology/term/${encodeURIComponent(term.term)}`
              );
              if (defRes.data) {
                definitions[term.term.toLowerCase()] = defRes.data;
              }
            } catch (e) {
              // Term not found, skip
            }
          }
          setTermDefinitions(definitions);
          
          // Cache the results
          termDefinitionCache.set(cacheKey, {
            terms: res.data.terms,
            definitions
          });
        }
      } catch (error) {
        console.error("Error detecting terms:", error);
      } finally {
        setIsProcessed(true);
      }
    };

    if (text && text.length > 20) {
      detectTerms();
    } else {
      setIsProcessed(true);
    }
  }, [text]);

  // If not processed yet or no terms, return plain text
  if (!isProcessed || detectedTerms.length === 0) {
    return <span>{text}</span>;
  }

  // Build highlighted text with tooltips
  const renderTextWithTooltips = () => {
    const result = [];
    let currentIndex = 0;
    const textLower = text.toLowerCase();
    
    // Sort terms by position to process in order
    const sortedTerms = [...detectedTerms]
      .filter(t => termDefinitions[t.term.toLowerCase()])
      .sort((a, b) => a.position - b.position);
    
    // Track which positions we've already highlighted
    const highlightedRanges = [];
    
    for (const termInfo of sortedTerms) {
      const termLower = termInfo.term.toLowerCase();
      const definition = termDefinitions[termLower];
      
      if (!definition) continue;
      
      // Find the term in the text
      let searchStart = currentIndex;
      let termIndex = textLower.indexOf(termLower, searchStart);
      
      // Check if this position overlaps with an already highlighted range
      while (termIndex !== -1) {
        const overlaps = highlightedRanges.some(
          ([start, end]) => termIndex >= start && termIndex < end
        );
        
        if (!overlaps) break;
        
        // Try to find the next occurrence
        termIndex = textLower.indexOf(termLower, termIndex + 1);
      }
      
      if (termIndex === -1) continue;
      
      // Add text before this term
      if (termIndex > currentIndex) {
        result.push(
          <span key={`text-${currentIndex}`}>
            {text.substring(currentIndex, termIndex)}
          </span>
        );
      }
      
      // Add the term with tooltip
      const termEnd = termIndex + termInfo.term.length;
      const originalTerm = text.substring(termIndex, termEnd);
      
      result.push(
        <TermTooltip 
          key={`term-${termIndex}`}
          term={originalTerm}
          definition={definition}
        />
      );
      
      // Track this highlighted range
      highlightedRanges.push([termIndex, termEnd]);
      currentIndex = termEnd;
    }
    
    // Add remaining text
    if (currentIndex < text.length) {
      result.push(
        <span key={`text-end`}>
          {text.substring(currentIndex)}
        </span>
      );
    }
    
    return result;
  };

  return <span>{renderTextWithTooltips()}</span>;
};

// Individual term tooltip component
const TermTooltip = ({ term, definition }) => {
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span 
            className="border-b border-dashed border-[#C5A059] text-[#C5A059] cursor-help hover:bg-[#C5A059]/10 transition-colors px-0.5 rounded-sm"
            data-testid={`term-tooltip-${term.toLowerCase().replace(/\s+/g, '-')}`}
          >
            {term}
          </span>
        </TooltipTrigger>
        <TooltipContent 
          side="top" 
          className="max-w-[350px] bg-[#15171C] border border-[#C5A059]/30 p-3 rounded-sm"
        >
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <BookOpen className="w-3 h-3 text-[#C5A059]" />
              <span className="font-mono text-xs text-[#C5A059] uppercase">
                {definition.category}
              </span>
              {definition.uk_specific && (
                <span className="text-[10px] bg-[#4A6FA5]/20 text-[#4A6FA5] px-1.5 py-0.5 rounded-sm font-mono">
                  UK
                </span>
              )}
            </div>
            <p className="text-[#E2E8F0] text-sm font-manrope leading-relaxed">
              {definition.definition}
            </p>
            {definition.us_equivalent && (
              <p className="text-[#64748B] text-xs font-mono">
                US: {definition.us_equivalent}
              </p>
            )}
            {definition.example_usage && (
              <p className="text-[#94A3B8] text-xs italic border-l-2 border-[#C5A059]/30 pl-2">
                "{definition.example_usage}"
              </p>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

const CitationCard = ({ citation }) => {
  const isCase = citation.citation || citation.court;
  
  return (
    <Card className="bg-[#0B0C10] border-white/5 rounded-sm p-3 hover:border-[#C5A059]/30 transition-colors">
      <div className="flex items-start gap-2">
        {isCase ? (
          <Gavel className="w-4 h-4 text-[#C5A059] shrink-0 mt-0.5" />
        ) : (
          <BookOpen className="w-4 h-4 text-[#C5A059] shrink-0 mt-0.5" />
        )}
        <div className="flex-1 min-w-0">
          {isCase ? (
            <>
              <p className="font-mono text-xs text-[#C5A059] mb-1">{citation.citation}</p>
              <p className="text-[#E2E8F0] text-sm font-manrope truncate">{citation.name}</p>
              <p className="text-[#64748B] text-xs font-mono mt-1">
                {citation.court} • {citation.date}
              </p>
            </>
          ) : (
            <>
              <p className="text-[#E2E8F0] text-sm font-manrope">{citation.title}</p>
              <p className="text-[#64748B] text-xs font-mono mt-1">
                {citation.type} • {citation.year}
              </p>
            </>
          )}
          {citation.uri && (
            <a 
              href={citation.uri} 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-[#4A6FA5] text-xs font-mono mt-2 flex items-center gap-1 hover:text-[#C5A059]"
            >
              View Source <ExternalLink className="w-3 h-3" />
            </a>
          )}
        </div>
      </div>
    </Card>
  );
};

export default ChatConsole;
